import React, { useEffect, useMemo } from 'react';
import {
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { DashboardWeeklyAttendance } from '../../components/DashboardWeeklyAttendance';
import { RoleSwitcher } from '../../components/RoleSwitcher';
import { LopMonthSummary } from '../../components/LopMonthSummary';
import { ResponsiveSplit } from '../../components/ResponsiveSplit';
import { getAttendanceCorrectionModuleCapabilities } from '../../config/attendanceCorrectionCapabilities';
import { getTimePermissionModuleCapabilities } from '../../config/timePermissionCapabilities';
import { getWfhModuleCapabilities } from '../../config/wfhCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import {
  routes,
  type MainTabParamList,
  type RootStackParamList,
} from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import {
  formatUnreadBadge,
  useNotificationCount,
} from '../../context/NotificationCountContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useDashboard } from '../../hooks/useDashboard';
import type { DashboardSection } from '../../types/dashboard';
import {
  getAttendanceStatusMessage,
  getTodayAttendanceStatusLabel,
} from '../../utils/attendanceStatus';
import {
  calendarDaysBetween,
  formatDurationMinutes,
  formatLeaveDuration,
  formatTime,
  getGreeting,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import { leaveBalanceDisplayColor } from '../../utils/leaveBalance';
import { formatHolidayDateAndDay } from '../../utils/officeCalendar';
import {
  checkedOutTotalWorkMinutes,
  formatPolicyHours,
  formatTotalWorkHours,
  resolveTotalHoursCoverage,
} from '../../utils/totalHours';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

type SectionErrorProps = {
  section: DashboardSection;
  message: string;
  onRetry: (section: DashboardSection) => void;
};

type QuickLink = {
  id: string;
  title: string;
  visible: boolean;
  onPress: () => void;
  tone: 'attendance' | 'leave' | 'wfh' | 'permission';
};

function SectionError({ section, message, onRetry }: SectionErrorProps) {
  const palette = useAppColors();
  return (
    <View style={styles.sectionError} accessibilityRole="alert">
      <Text style={[styles.errorTextBase, { color: palette.error }]}>{message}</Text>
      <AppButton title="Retry" variant="secondary" onPress={() => onRetry(section)} />
    </View>
  );
}

function displayClock(value: string | null | undefined): string {
  if (!value) return '--';
  const formatted = formatTime(value);
  return formatted === 'Not available' ? '--' : formatted;
}

function displayDuration(value: number | null | undefined): string {
  const formatted = formatDurationMinutes(value ?? null);
  return formatted === 'Not available' ? '--' : formatted;
}

function holidayCountdownLabel(holidayDate: string, today: string): string | null {
  const days = calendarDaysBetween(today, holidayDate);
  if (days == null || days < 0) return null;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

function splitTodayLabel(date = new Date()) {
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(
    date,
  );
  const dateLine = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  return { weekday, dateLine };
}

function statusTone(
  label: string,
  palette: ColorPalette,
): { color: string; mark: string } {
  const normalized = label.trim().toLowerCase();
  if (
    normalized.includes('present') ||
    normalized.includes('checked in') ||
    normalized.includes('day completed')
  ) {
    return { color: palette.success, mark: '✓' };
  }
  if (normalized.includes('absent') || normalized === 'lop' || normalized.startsWith('lop')) {
    return { color: palette.error, mark: '!' };
  }
  if (normalized.includes('leave') || normalized.includes('late')) {
    return { color: palette.warning, mark: '•' };
  }
  if (normalized.includes('wfh') || normalized.includes('working from home')) {
    return { color: palette.info, mark: '•' };
  }
  return { color: palette.textSecondary, mark: '•' };
}

function toneColors(tone: QuickLink['tone'], palette: ColorPalette) {
  switch (tone) {
    case 'leave':
      return {
        backgroundColor: `${palette.error}14`,
        iconColor: palette.error,
      };
    case 'wfh':
      return {
        backgroundColor: `${palette.success}14`,
        iconColor: palette.success,
      };
    case 'permission':
      return {
        backgroundColor: `${palette.warning}14`,
        iconColor: palette.warning,
      };
    default:
      return {
        backgroundColor: `${palette.info}14`,
        iconColor: palette.info,
      };
  }
}

function QuickLinkIcon({
  tone,
  color,
}: {
  tone: QuickLink['tone'];
  color: string;
}) {
  if (tone === 'leave') {
    return (
      <View style={iconStyles.root} accessibilityElementsHidden>
        <View style={[iconStyles.door, { borderColor: color }]} />
        <View style={[iconStyles.doorKnob, { backgroundColor: color }]} />
      </View>
    );
  }
  if (tone === 'wfh') {
    return (
      <View style={iconStyles.root} accessibilityElementsHidden>
        <View style={[iconStyles.homeRoof, { borderBottomColor: color }]} />
        <View style={[iconStyles.homeBody, { borderColor: color }]} />
      </View>
    );
  }
  if (tone === 'permission') {
    return (
      <View style={iconStyles.root} accessibilityElementsHidden>
        <View style={[iconStyles.badgeOuter, { borderColor: color }]}>
          <View style={[iconStyles.badgeInner, { backgroundColor: color }]} />
        </View>
      </View>
    );
  }
  return (
    <View style={iconStyles.root} accessibilityElementsHidden>
      <View style={[iconStyles.clipboard, { borderColor: color }]}>
        <View style={[iconStyles.clipboardClip, { backgroundColor: color }]} />
        <View style={[iconStyles.clipboardLine, { backgroundColor: color }]} />
        <View style={[iconStyles.clipboardLine, { backgroundColor: color }]} />
      </View>
    </View>
  );
}

function BellIcon({ color }: { color: string }) {
  return (
    <View style={styles.bellRoot} accessibilityElementsHidden>
      <View style={[styles.bellBody, { borderColor: color }]} />
      <View style={[styles.bellClapper, { backgroundColor: color }]} />
    </View>
  );
}

function CalendarGlyph({ color }: { color: string }) {
  return (
    <View style={styles.calendarGlyph} accessibilityElementsHidden>
      <View style={[styles.calendarBox, { borderColor: color }]}>
        <View style={[styles.calendarTop, { backgroundColor: color }]} />
        <View style={styles.calendarDots}>
          <View style={[styles.calendarDot, { backgroundColor: color }]} />
          <View style={[styles.calendarDot, { backgroundColor: color }]} />
          <View style={[styles.calendarDot, { backgroundColor: color }]} />
        </View>
      </View>
    </View>
  );
}

function formatMetric(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '--';
  return String(value).padStart(2, '0');
}

export function DashboardScreen() {
  const auth = useAuth();
  const palette = useAppColors();
  const { isCompact, isWide, monthItemWidth } = useResponsiveLayout();
  const monthCardWidth = isWide ? '48%' : monthItemWidth;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const tabNavigation =
    useNavigation<BottomTabNavigationProp<MainTabParamList>>();
  const dashboard = useDashboard(auth.permissions, auth.user?.userId);
  const notificationCount = useNotificationCount();
  const { syncUnreadCount } = notificationCount;
  const attendance = dashboard.data?.attendance;
  // Total Work Hours / Not Covered only after a valid check-out.
  const totalWorkMinutes = attendance
    ? checkedOutTotalWorkMinutes(attendance)
    : null;
  const errors = dashboard.sectionErrors;
  const displayName = auth.user?.displayName || auth.user?.username || 'there';
  const wfhCapabilities = getWfhModuleCapabilities(auth.permissions);
  const timePermissionCapabilities = getTimePermissionModuleCapabilities(
    auth.permissions,
  );
  const correctionCapabilities = getAttendanceCorrectionModuleCapabilities(
    auth.permissions,
  );
  const todayParts = splitTodayLabel();
  const statusLabel = getTodayAttendanceStatusLabel(attendance);
  const statusStyle = statusTone(statusLabel, palette);

  const themed = useMemo(
    () => ({
      topTitle: {
        ...typography.sectionTitle,
        color: palette.white,
        fontWeight: '700' as const,
      },
      greeting: { ...typography.body, color: palette.textSecondary },
      name: {
        ...typography.screenTitle,
        color: palette.textPrimary,
        fontSize: 26,
        lineHeight: 32,
      },
      role: { ...typography.body, color: palette.textMuted },
      weekday: {
        ...typography.caption,
        color: palette.textMuted,
        textAlign: 'right' as const,
      },
      dateLine: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
        textAlign: 'right' as const,
      },
      sectionTitle: { ...typography.sectionTitle, color: palette.textPrimary },
      statusLabel: {
        ...typography.body,
        color: statusStyle.color,
        fontWeight: '700' as const,
      },
      statusMessage: { ...typography.caption, color: palette.textSecondary },
      timeLabel: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      timeValue: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      quickLabel: {
        ...typography.caption,
        color: palette.textPrimary,
        fontWeight: '600' as const,
        textAlign: 'center' as const,
      },
      metricValue: {
        ...typography.screenTitle,
        fontSize: 24,
        lineHeight: 30,
        fontWeight: '700' as const,
      },
      metricLabel: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      holidayTitle: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      holidayMeta: { ...typography.caption, color: palette.textMuted },
      holidayCountdown: {
        ...typography.caption,
        color: palette.success,
        fontWeight: '700' as const,
      },
      balance: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      body: { ...typography.body, color: palette.textSecondary },
      metric: { ...typography.screenTitle, color: palette.primary },
      updated: {
        ...typography.caption,
        color: palette.textMuted,
        textAlign: 'center' as const,
        marginVertical: spacing.md,
      },
      badge: { backgroundColor: palette.error },
      badgeText: {
        ...typography.caption,
        color: palette.onError,
        fontWeight: '700' as const,
        fontSize: 10,
        lineHeight: 12,
      },
    }),
    [palette, statusStyle.color],
  );

  useEffect(() => {
    if (errors.notifications) {
      syncUnreadCount(null);
      return;
    }
    if (dashboard.data?.notifications.unreadCount != null) {
      syncUnreadCount(dashboard.data.notifications.unreadCount);
    }
  }, [
    dashboard.data?.notifications.unreadCount,
    errors.notifications,
    syncUnreadCount,
  ]);

  const sharedUnread =
    notificationCount.unreadCount ??
    dashboard.data?.notifications.unreadCount ??
    null;
  const unreadBadge = formatUnreadBadge(sharedUnread);

  if (dashboard.isLoading && !dashboard.data) {
    return (
      <AppScreen contentStyle={styles.center}>
        <AppLoader message="Loading your dashboard" />
      </AppScreen>
    );
  }

  if (dashboard.error) {
    return (
      <AppScreen contentStyle={styles.center}>
        <ErrorState message={dashboard.error} onRetry={dashboard.loadDashboard} />
      </AppScreen>
    );
  }

  const quickLinks: QuickLink[] = [
    {
      id: 'attendance',
      title: 'Attendance',
      tone: 'attendance',
      visible: auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW),
      onPress: () => tabNavigation.navigate(routes.attendance),
    },
    {
      id: 'leave',
      title: 'Leave',
      tone: 'leave',
      visible: auth.hasPermission(PERMISSIONS.LEAVE_APPLY),
      onPress: () => tabNavigation.navigate(routes.leave),
    },
    {
      id: 'wfh',
      title: 'WFH',
      tone: 'wfh',
      visible: wfhCapabilities.canViewHistory,
      onPress: () => navigation.navigate(routes.wfhHome),
    },
    {
      id: 'permission',
      title: 'Permission',
      tone: 'permission',
      visible:
        timePermissionCapabilities.canViewHistory,
      onPress: () => navigation.navigate(routes.timePermissionHome),
    },
    {
      id: 'correction',
      title: 'Correction',
      tone: 'attendance',
      visible: correctionCapabilities.canApply,
      onPress: () => navigation.navigate(routes.attendanceCorrectionHome),
    },
  ];
  const visibleQuickLinks = quickLinks.filter(link => link.visible);
  const monthOverview = dashboard.data?.monthOverview;
  const weeklyAttendance = dashboard.data?.weeklyAttendance;
  const upcomingHolidays = dashboard.data?.upcomingHolidays?.holidays ?? [];
  const holidayToday = todayBusinessCalendarDate();
  const monthMetrics = [
    {
      id: 'present',
      label: 'Present',
      value: monthOverview?.present ?? null,
      color: palette.success,
    },
    {
      id: 'absent',
      label: 'LOP',
      value: monthOverview?.absent ?? null,
      color: palette.error,
    },
    {
      id: 'leave',
      label: 'Leave',
      value: monthOverview?.leave ?? null,
      color: palette.warning,
    },
    {
      id: 'wfh',
      label: 'WFH',
      value: monthOverview?.wfh ?? null,
      color: palette.info,
    },
  ];

  return (
    <AppScreen
      scroll
      contentStyle={styles.screen}
      refreshControl={
        <RefreshControl
          refreshing={dashboard.isRefreshing}
          onRefresh={dashboard.refreshDashboard}
          tintColor={palette.primary}
          accessibilityLabel="Refresh dashboard"
        />
      }
    >
      <View style={[styles.topBar, { backgroundColor: palette.brand }]}>
        <Text
          accessibilityRole="header"
          numberOfLines={1}
          style={[themed.topTitle, styles.topTitle]}
        >
          Dashboard
        </Text>
        <View style={styles.topActions}>
          <RoleSwitcher />
          <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            unreadBadge
              ? `Unread notifications, ${unreadBadge}`
              : 'Unread notifications'
          }
          onPress={() => navigation.navigate(routes.notificationList)}
          style={styles.bellButton}
        >
          <BellIcon color={palette.white} />
          {unreadBadge ? (
            <View style={[styles.unreadBadge, themed.badge]}>
              <Text style={themed.badgeText}>{unreadBadge}</Text>
            </View>
          ) : null}
        </Pressable>
        </View>
      </View>

      <View style={styles.body}>
        <View style={[styles.welcomeRow, isCompact ? styles.welcomeRowStacked : null]}>
          <View style={styles.welcomeText}>
            <Text style={themed.greeting}>{getGreeting()},</Text>
            <Text style={themed.name}>{displayName}</Text>
          </View>
          <View style={[styles.welcomeDate, isCompact ? styles.welcomeDateStacked : null]}>
            <Text style={[themed.weekday, isCompact ? styles.welcomeDateTextLeft : null]}>
              {todayParts.weekday}
            </Text>
            <Text style={[themed.dateLine, isCompact ? styles.welcomeDateTextLeft : null]}>
              {todayParts.dateLine}
            </Text>
          </View>
        </View>

        <ResponsiveSplit>
          <View style={styles.splitPane}>
            {auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW) ? (
              <AppCard style={styles.card}>
                <View style={styles.cardHeader}>
                  <Text style={themed.sectionTitle}>Today's Status</Text>
                  <CalendarGlyph color={palette.textMuted} />
                </View>
                {errors.attendance ? (
                  <SectionError
                    section="attendance"
                    message={errors.attendance.message}
                    onRetry={dashboard.retrySection}
                  />
                ) : attendance ? (
                  <>
                    <View style={styles.statusRow}>
                      <View
                        style={[
                          styles.statusMark,
                          { backgroundColor: `${statusStyle.color}22` },
                        ]}
                      >
                        <Text style={{ color: statusStyle.color, fontWeight: '700' }}>
                          {statusStyle.mark}
                        </Text>
                      </View>
                      <Text
                        accessibilityLabel={`Attendance status: ${statusLabel}`}
                        style={themed.statusLabel}
                      >
                        {statusLabel}
                      </Text>
                    </View>
                    <Text style={themed.statusMessage}>
                      {getAttendanceStatusMessage(attendance.currentStatus)}
                    </Text>
                    {attendance.dayStatus?.notes.length ? (
                      <Text style={themed.statusMessage}>
                        {attendance.dayStatus.notes.join('\n')}
                      </Text>
                    ) : null}
                    <View style={styles.timeRow}>
                      <View
                        style={[
                          styles.timeBox,
                          { backgroundColor: palette.background },
                        ]}
                      >
                        <Text style={themed.timeLabel}>Check In</Text>
                        <Text style={themed.timeValue}>
                          {displayClock(attendance.checkInTime)}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.timeBox,
                          { backgroundColor: palette.background },
                        ]}
                      >
                        <Text style={themed.timeLabel}>Check Out</Text>
                        <Text style={themed.timeValue}>
                          {displayClock(attendance.checkOutTime)}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.timeRow}>
                      <View
                        style={[
                          styles.timeBox,
                          { backgroundColor: palette.background },
                        ]}
                      >
                        <Text style={themed.timeLabel}>Total Break Hours</Text>
                        <Text
                          accessibilityLabel={`Total Break Hours ${displayDuration(
                            attendance.breakMinutes,
                          )}`}
                          style={themed.timeValue}
                        >
                          {displayDuration(attendance.breakMinutes)}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.timeBox,
                          { backgroundColor: palette.background },
                        ]}
                      >
                        <Text style={themed.timeLabel}>Expected Work Hours</Text>
                        <Text
                          accessibilityLabel={`Expected Work Hours ${formatPolicyHours(
                            attendance.expectedWorkMinutes,
                            '--',
                          )}`}
                          style={themed.timeValue}
                        >
                          {formatPolicyHours(attendance.expectedWorkMinutes, '--')}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.timeRow}>
                      <View
                        style={[
                          styles.timeBox,
                          { backgroundColor: palette.background },
                        ]}
                      >
                        <Text style={themed.timeLabel}>Total Work Hours</Text>
                        <Text
                          accessibilityLabel={`Total Work Hours ${formatTotalWorkHours(
                            totalWorkMinutes,
                            '--',
                          )}`}
                          style={[
                            themed.timeValue,
                            resolveTotalHoursCoverage(
                              totalWorkMinutes,
                              attendance.spreadMinutes,
                            ) === 'not_covered'
                              ? { color: palette.error }
                              : null,
                          ]}
                        >
                          {formatTotalWorkHours(totalWorkMinutes, '--')}
                        </Text>
                      </View>
                    </View>
                  </>
                ) : (
                  <EmptyState
                    title="Attendance unavailable"
                    message="Today’s attendance information is not available."
                  />
                )}
              </AppCard>
            ) : null}

            {visibleQuickLinks.length ? (
              <View style={styles.quickRow}>
                {visibleQuickLinks.map(link => {
                  const colors = toneColors(link.tone, palette);
                  return (
                    <Pressable
                      key={link.id}
                      accessibilityRole="button"
                      accessibilityLabel={link.title}
                      onPress={link.onPress}
                      style={styles.quickItem}
                    >
                      <View
                        style={[
                          styles.quickIcon,
                          { backgroundColor: colors.backgroundColor },
                        ]}
                      >
                        <QuickLinkIcon tone={link.tone} color={colors.iconColor} />
                      </View>
                      <Text style={themed.quickLabel}>{link.title}</Text>
                    </Pressable>
                  );
                })}
              </View>
            ) : null}
          </View>

          <View style={styles.splitPane}>
            {weeklyAttendance?.visible || errors.weeklyAttendance ? (
              <DashboardWeeklyAttendance
                report={weeklyAttendance?.report ?? null}
                errorMessage={errors.weeklyAttendance?.message}
                onRetry={() => dashboard.retrySection('weeklyAttendance')}
                onViewDetails={() =>
                  navigation.navigate(routes.myWeeklyAttendance)
                }
              />
            ) : null}

            {monthOverview?.visible || errors.monthOverview ? (
              <>
                <Text style={themed.sectionTitle}>This Month Overview</Text>
                {errors.monthOverview ? (
                  <SectionError
                    section="monthOverview"
                    message={errors.monthOverview.message}
                    onRetry={dashboard.retrySection}
                  />
                ) : (
                  <View style={styles.monthRow}>
                    {monthMetrics.map(metric => (
                      <AppCard
                        key={metric.id}
                        style={[styles.monthCard, { width: monthCardWidth }]}
                      >
                        <Text
                          style={[themed.metricValue, { color: metric.color }]}
                          accessibilityLabel={`${metric.label} ${formatMetric(metric.value)}`}
                        >
                          {formatMetric(metric.value)}
                        </Text>
                        <Text style={themed.metricLabel}>{metric.label}</Text>
                      </AppCard>
                    ))}
                  </View>
                )}
              </>
            ) : null}

            <Text style={themed.sectionTitle}>Upcoming Holiday</Text>
            {errors.upcomingHolidays ? (
              <SectionError
                section="upcomingHolidays"
                message={errors.upcomingHolidays.message}
                onRetry={dashboard.retrySection}
              />
            ) : upcomingHolidays.length ? (
              upcomingHolidays.map(holiday => {
                const countdown = holidayCountdownLabel(
                  holiday.holidayDate,
                  holidayToday,
                );
                return (
                  <AppCard key={holiday.id} style={styles.holidayCard}>
                    <View
                      style={[
                        styles.holidayIcon,
                        { backgroundColor: `${palette.primary}14` },
                      ]}
                    >
                      <View
                        style={[styles.monument, { borderColor: palette.primary }]}
                      />
                    </View>
                    <View style={styles.holidayText}>
                      <Text style={themed.holidayTitle}>{holiday.holidayName}</Text>
                      <Text style={themed.holidayMeta}>
                        {formatHolidayDateAndDay(
                          holiday.holidayDate,
                          holiday.dayName,
                          holiday.displayDate,
                        )}
                      </Text>
                      {countdown ? (
                        <Text style={themed.holidayCountdown}>{countdown}</Text>
                      ) : null}
                    </View>
                  </AppCard>
                );
              })
            ) : (
              <AppCard style={styles.holidayCard}>
                <View
                  style={[
                    styles.holidayIcon,
                    { backgroundColor: `${palette.primary}14` },
                  ]}
                >
                  <View style={[styles.monument, { borderColor: palette.primary }]} />
                </View>
                <View style={styles.holidayText}>
                  <Text style={themed.holidayTitle}>No upcoming holiday</Text>
                  <Text style={themed.holidayMeta}>
                    Holiday details will appear when available.
                  </Text>
                </View>
              </AppCard>
            )}

            {dashboard.data?.approvals.visible ? (
              <AppCard style={styles.card}>
                <Text style={themed.sectionTitle}>Pending approvals</Text>
                {errors.approvals ? (
                  <SectionError
                    section="approvals"
                    message={errors.approvals.message}
                    onRetry={dashboard.retrySection}
                  />
                ) : (
                  <Text style={themed.metric}>
                    {dashboard.data.approvals.count ?? 'Not available'}
                  </Text>
                )}
              </AppCard>
            ) : null}

            {dashboard.data?.leaveBalance.visible ? (
              <AppCard style={styles.card}>
                <Text style={themed.sectionTitle}>Leave balance</Text>
                {errors.leaveBalance ? (
                  <SectionError
                    section="leaveBalance"
                    message={errors.leaveBalance.message}
                    onRetry={dashboard.retrySection}
                  />
                ) : dashboard.data.leaveBalance.balances.length ? (
                  dashboard.data.leaveBalance.balances.map(balance => (
                    <View key={balance.id} style={styles.balanceRow}>
                      <Text style={themed.body}>{balance.name}</Text>
                      <Text
                        style={[
                          themed.balance,
                          {
                            color: leaveBalanceDisplayColor(
                              balance.available,
                              palette.textPrimary,
                              palette.error,
                            ),
                          },
                        ]}
                      >
                        {formatLeaveDuration(balance.available)}
                      </Text>
                    </View>
                  ))
                ) : (
                  <Text style={themed.body}>No leave balances are available.</Text>
                )}
                {!errors.leaveBalance && dashboard.data.leaveBalance.lop ? (
                  <LopMonthSummary
                    summary={dashboard.data.leaveBalance.lop}
                    palette={palette}
                  />
                ) : null}
              </AppCard>
            ) : null}
          </View>
        </ResponsiveSplit>

        <Text style={themed.updated}>
          {dashboard.lastUpdatedAt
            ? `Last updated ${formatTime(dashboard.lastUpdatedAt.toISOString())}`
            : 'Not updated yet'}
        </Text>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  screen: { paddingHorizontal: 0, paddingVertical: 0 },
  center: { justifyContent: 'center' },
  topBar: {
    minHeight: 56,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  topTitle: { flexShrink: 1 },
  topActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    flexShrink: 1,
    minWidth: 0,
  },
  bellButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bellRoot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  bellBody: {
    width: 16,
    height: 14,
    borderWidth: 2,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  bellClapper: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 1,
  },
  unreadBadge: {
    position: 'absolute',
    top: 4,
    right: 2,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  body: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
  splitPane: { gap: spacing.md, minWidth: 0, width: '100%' },
  welcomeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.xs,
    minWidth: 0,
  },
  welcomeRowStacked: {
    flexDirection: 'column',
  },
  welcomeText: { flex: 1, gap: 2, minWidth: 0 },
  welcomeDate: { alignItems: 'flex-end', gap: 2, paddingTop: 4 },
  welcomeDateStacked: { alignItems: 'flex-start', paddingTop: 0 },
  welcomeDateTextLeft: { textAlign: 'left' as const },
  card: { gap: spacing.md, marginBottom: 0 },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  statusMark: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  timeBox: {
    flexGrow: 1,
    flexBasis: 140,
    minWidth: 140,
    maxWidth: '100%',
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    gap: spacing.xs,
  },
  quickRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  quickItem: {
    flexGrow: 1,
    flexBasis: 72,
    minWidth: 72,
    maxWidth: '100%',
    alignItems: 'center',
    gap: spacing.sm,
  },
  quickIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  monthRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  monthCard: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.sm,
    minHeight: 92,
    minWidth: 0,
    maxWidth: '100%',
    flexGrow: 0,
  },
  holidayCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  holidayIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  monument: {
    width: 18,
    height: 22,
    borderWidth: 2,
    borderTopWidth: 0,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  holidayText: { flex: 1, gap: 2 },
  balanceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: spacing.md,
    minWidth: 0,
  },
  sectionError: { gap: spacing.sm },
  errorTextBase: { ...typography.caption },
  calendarGlyph: { width: 22, height: 22, alignItems: 'center' },
  calendarBox: {
    width: 18,
    height: 18,
    borderWidth: 1.5,
    borderRadius: 4,
    overflow: 'hidden',
  },
  calendarTop: { height: 4 },
  calendarDots: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  calendarDot: { width: 2, height: 2, borderRadius: 1 },
});

const iconStyles = StyleSheet.create({
  root: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clipboard: {
    width: 16,
    height: 20,
    borderWidth: 2,
    borderRadius: 3,
    alignItems: 'center',
    paddingTop: 5,
    gap: 2,
  },
  clipboardClip: {
    position: 'absolute',
    top: -3,
    width: 8,
    height: 4,
    borderRadius: 2,
  },
  clipboardLine: {
    width: 8,
    height: 2,
    borderRadius: 1,
  },
  door: {
    width: 14,
    height: 20,
    borderWidth: 2,
    borderRadius: 2,
  },
  doorKnob: {
    position: 'absolute',
    right: 9,
    width: 3,
    height: 3,
    borderRadius: 1.5,
  },
  homeRoof: {
    width: 0,
    height: 0,
    borderLeftWidth: 11,
    borderRightWidth: 11,
    borderBottomWidth: 8,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    marginBottom: 1,
  },
  homeBody: {
    width: 16,
    height: 12,
    borderWidth: 2,
    borderTopWidth: 0,
  },
  badgeOuter: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeInner: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
});
